/**
 * Countdown Cell Renderer
 *
 * Generates HTML for countdown/close time display
 */

import type { Lot } from '../../types';
import { formatCountdown, fmtDate, escapeHtml, escapeAttr } from '../formatting';
import styles from './tableCells.module.css';

export function buildCountdownCell(
  lot: Lot,
  effectiveCloseAt: string | number | null | undefined,
  hasCustomTime: boolean
): string {
  const l = lot;

  // Convert to timestamp for formatCountdown
  const effectiveCloseTs =
    typeof effectiveCloseAt === 'number'
      ? effectiveCloseAt
      : typeof effectiveCloseAt === 'string'
        ? new Date(effectiveCloseAt).getTime()
        : null;
  const countdownObj = formatCountdown(effectiveCloseTs);
  const countdownBaseClass = styles.countdownCell;
  const countdownUrgentClass = countdownObj.isUrgent ? ' ' + styles.countdownUrgent : '';
  const liveFinishBadge =
    l.liveFinish || l.liveFinishDateRange
      ? '<div class="' + styles.liveFinishBadge + '">Live Finish</div>'
      : '';
  let countdownDisplay;

  if ((l.liveFinish || l.liveFinishDateRange) && (l.liveFinishDateRange || effectiveCloseAt)) {
    const rangeText = l.liveFinishDateRange
      ? escapeHtml(l.liveFinishDateRange)
      : fmtDate(effectiveCloseAt).split(',')[0];
    const editCloseBtn =
      '<button class="' +
      styles.closeTimeEditBtn +
      '" data-action="edit-close-time" data-id="' +
      escapeAttr(l.id) +
      '" title="Edit close time" aria-label="Edit close time">✏️</button>';
    countdownDisplay =
      '<div class="' +
      countdownBaseClass +
      '" data-close-ts="' +
      (effectiveCloseAt || '') +
      '">' +
      '<div class="' +
      styles.countdownDate +
      ' ' +
      styles.countdownDateCompact +
      '">' +
      rangeText +
      '</div>' +
      liveFinishBadge +
      (hasCustomTime ? '<div class="' + styles.customTimeBadge + '">Custom ⚙️</div>' : '') +
      editCloseBtn +
      '</div>';
  } else if (effectiveCloseAt) {
    // Regular timed auction: show countdown + date (clickable to edit)
    const editCloseBtn =
      '<button class="' +
      styles.closeTimeEditBtn +
      '" data-action="edit-close-time" data-id="' +
      escapeAttr(l.id) +
      '" title="Edit close time" aria-label="Edit close time">✏️</button>';
    countdownDisplay =
      '<div class="' +
      countdownBaseClass +
      countdownUrgentClass +
      '" data-close-ts="' +
      effectiveCloseAt +
      '">' +
      '<div class="' +
      styles.countdownTime +
      '">' +
      countdownObj.text +
      '</div>' +
      '<div class="' +
      styles.countdownDate +
      '">' +
      fmtDate(effectiveCloseAt) +
      '</div>' +
      (hasCustomTime ? '<div class="' + styles.customTimeBadge + '">Custom ⚙️</div>' : '') +
      editCloseBtn +
      '</div>';
  } else {
    // No close time: show "?" button
    countdownDisplay =
      '<button class="' +
      styles.closeTimeSetBtn +
      '" data-action="edit-close-time" data-id="' +
      escapeAttr(l.id) +
      '" title="Set close time" aria-label="Set close time">?</button>';
  }

  return countdownDisplay;
}
